import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Container,
  Section,
  SectionTitle,
  ListItem,
  Row,
  RowColumn,
  List,
} from 'chaoskit/src/components'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import ActionCard from '~components/ActionCard'
import Data from '~components/Data'
import defaultPressLogo from '~media/global/mascot.svg'
import Press from '~layouts/PressLayout'
import HelmetSEO from '~partials/global/HelmetSEO'

const StylesPressAboutVariables = (theme) => ({
  timeline: {
    offset: theme.space.xlarge + theme.space.large,
    bulletSize: 8,
  },
})

const TimelineItem = (props) => {
  const theme = useTheme()

  return (
    <ListItem
      css={{
        position: 'relative',
        paddingLeft: StylesPressAboutVariables(theme).timeline.offset,

        '&::before': {
          content: 'attr(data-year)',
          position: 'absolute',
          top: 0,
          left: 0,
          fontWeight: theme.fontWeight.bold,
          lineHeight: theme.lineHeight.base,
        },

        '&::after': {
          content: "''",
          position: 'absolute',
          top:
            (theme.lineHeight.base * theme.fontSize.base) / 2 -
            StylesPressAboutVariables(theme).timeline.bulletSize / 2,
          left: StylesPressAboutVariables(theme).timeline.offset / 1.5 + 1,
          width: StylesPressAboutVariables(theme).timeline.bulletSize,
          height: StylesPressAboutVariables(theme).timeline.bulletSize,
          background: theme.color.primary.base,
          borderRadius: theme.borderRadius.rounded,
          transform: 'translateX(-50%)',
          boxShadow: `0 0 0 10px ${theme.color.light.base}`,
        },
      }}
      {...props}
    />
  )
}

const PressItem = ({ data }) => (
  <ActionCard photo={data.logo || defaultPressLogo} hover url={data.link}>
    <Data>{data.title}</Data>
  </ActionCard>
)

PressItem.propTypes = {
  data: PropTypes.object.isRequired,
}

const PressAbout = ({
  data: {
    pageData: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <Press>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <Section space="xlarge">
        <Container>
          <SectionTitle
            title={<Data>{pageData.section1.heading}</Data>}
            sub={<Data>{pageData.section1.blurb}</Data>}
          />
        </Container>
      </Section>
      <Section
        space="xlarge"
        slant="bottom-shadow"
        css={{ background: theme.color.panel.base }}
      >
        <Container>
          <SectionTitle title={<Data>{pageData.featuredPress.heading}</Data>} />
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,

              [theme.mq.small]: {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            }}
          >
            {pageData.featuredPress.items.map((item) => (
              <PressItem data={item} key={item.link} />
            ))}
          </div>
        </Container>
      </Section>
      <Section space="xlarge">
        <Container>
          <SectionTitle title={<Data>{pageData.timeline.heading}</Data>} />
          <Row css={{ justifyContent: 'center' }}>
            <RowColumn size={{ medium: 9 }}>
              <List
                as="ol"
                space="large"
                css={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  position: 'relative',

                  '&::before': {
                    content: "''",
                    position: 'absolute',
                    top: 0,
                    height: '100%',
                    width: 2,
                    background: theme.color.border.base,
                    left:
                      StylesPressAboutVariables(theme).timeline.offset / 1.5,
                  },
                }}
              >
                {pageData.timeline.items.map((event) => (
                  <TimelineItem
                    key={`${event.year}-${event.month}`}
                    data-year={event.year}
                  >
                    <div>
                      <MDXRenderer>{event.content}</MDXRenderer>
                      <time
                        css={{
                          fontWeight: theme.fontWeight.bold,
                          fontSize: theme.fontSize.small,
                          color: theme.fontColor.muted,
                          letterSpacing: theme.letterSpacing.extended,
                          textTransform: 'uppercase',
                        }}
                      >
                        {event.month}
                      </time>
                    </div>
                  </TimelineItem>
                ))}
              </List>
            </RowColumn>
          </Row>
        </Container>
      </Section>
    </Press>
  )
}

PressAbout.propTypes = {
  data: PropTypes.object.isRequired,
}

export default PressAbout

export const pageQuery = graphql`
  query PressAboutPageData {
    pageData: file(
      name: { eq: "press__about" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          section1 {
            blurb
            heading
          }
          featuredPress {
            heading
            items {
              title
              logo
              link
            }
          }
          timeline {
            heading
            items {
              content
              month: date(formatString: "MMMM")
              year: date(formatString: "YYYY")
            }
          }
        }
      }
    }
  }
`
